/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import { useId } from "react";
import { TextLink } from "ui/TextLink";
import { changeStyleWithHosting } from "utility/hosting";

import type { ChangeEvent, FC } from "react";

type Props = {
  isAgreed: boolean;
  onChange: (isAgreed: boolean) => void;
};

export const AgreementCheckbox: FC<Props> = ({ isAgreed, onChange }) => {
  const { t } = useI18n();
  const agreementId = useId();
  const classes = styles();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.currentTarget.checked);
  };

  return (
    <label htmlFor={agreementId} css={classes.root}>
      <input
        id={agreementId}
        type="checkbox"
        checked={isAgreed}
        css={classes.input}
        onChange={handleChange}
      />
      <span css={classes.checkbox} aria-hidden="true" />
      <span css={classes.label}>
        <span>{t("event.resale.entry.confirmation.agreementPrefix")}</span>
        <TextLink href="/terms-of-service">
          {t("common.footer.termsOfService")}
        </TextLink>
        <span>{t("event.resale.entry.confirmation.agreementSuffix")}</span>
      </span>
    </label>
  );
};

const styles = () => {
  const { accentColor } = changeStyleWithHosting().baseStyles;

  return {
    root: css`
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 24px;
      color: #222;
      font-size: 12px;
      line-height: 1.5;
      cursor: pointer;
    `,
    input: css`
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      border: 0;
      clip: rect(0, 0, 0, 0);
      overflow: hidden;
      white-space: nowrap;
      &:checked + span {
        background-color: ${accentColor};
        border-color: ${accentColor};
      }
      &:checked + span::after {
        content: "";
        position: absolute;
        top: 1px;
        left: 4px;
        width: 5px;
        height: 9px;
        border-right: 2px solid #fff;
        border-bottom: 2px solid #fff;
        transform: rotate(45deg);
      }
      &:focus-visible + span {
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 4px ${accentColor};
      }
    `,
    checkbox: css`
      position: relative;
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      background-color: #fff;
      border: 1px solid #bbb;
      border-radius: 2px;
    `,
    label: css`
      min-width: 0;
    `,
  };
};
